import { FormatNumberOptions, useIntl } from 'react-intl';
import { useSelector } from '../store/utils';
import { fractionDigitsCurrency } from './currencies';

export type FormatNumberOpts = FormatNumberOptions & {
  unit?: keyof typeof customUnits | 'celsius';
  fractionDigitsOffset?: number;
};

const customUnits = {
  'kilowatt-hour': (value: string) => `${value} kWh`,
  'megawatt-hour': (value: string) => `${value} MWh`,
  kilowatt: (value: string) => `${value} kW`,
  'kilowatt-peak': (value: string) => `${value} kWp`,
  'kilovoltampere-reactive': (value: string) => `${value} kVAr`,
  kilovoltampere: (value: string) => `${value} kVA`,
  ampere: (value: string) => `${value} A`,
  litre: (value: string) => `${value} l`,
  'meter-per-second': (value: string) => `${value} m/s`,
  voltage: (value: string) => `${value} V`,
  kilometer: (value: string) => `${value} km`,
};

const isCustomUnit = (unit: string): unit is keyof typeof customUnits =>
  unit in customUnits;

export const useFormatNumber = () => {
  const { formatNumber } = useIntl();
  const defaultCurrency = useSelector(({ variables }) => variables.CURRENCY);

  return (value: number, opts?: FormatNumberOpts) => {
    // adjust number value
    let adjustedValue = value;
    if (opts?.style === 'percent') adjustedValue = adjustedValue / 100;

    if (opts?.style === 'currency' && !opts.currency) {
      opts.currency = defaultCurrency;

      const fractionDigitsOffset = opts.fractionDigitsOffset || 0;

      opts.maximumFractionDigits =
        opts.maximumFractionDigits ??
        fractionDigitsCurrency[defaultCurrency] + fractionDigitsOffset;
      opts.minimumFractionDigits =
        opts.minimumFractionDigits ??
        fractionDigitsCurrency[defaultCurrency] + fractionDigitsOffset;
    }

    // adjust Int.formatNumber options
    const formatNumberOpts = {
      maximumFractionDigits: 2,
      ...(opts || {}),
    };

    const customUnit =
      formatNumberOpts?.style === 'unit' &&
      formatNumberOpts.unit &&
      isCustomUnit(formatNumberOpts.unit) &&
      formatNumberOpts.unit;

    if (customUnit) {
      formatNumberOpts.style = 'decimal';
      formatNumberOpts.unit = undefined;
    }

    let formatted = formatNumber(adjustedValue, formatNumberOpts);

    // format custom units
    if (customUnit) {
      formatted = customUnits[customUnit](formatted);
    }

    return formatted;
  };
};
